import React, { useRef } from 'react';
import Button from '@material-ui/core/Button';
import { makeStyles, fade } from '@material-ui/core/styles';
import Popover from '@material-ui/core/Popover';
import DialogContent from '@material-ui/core/DialogContent';
import Typography from '@material-ui/core/Typography';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import MenuTitleBar from './MenuTitleBar';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedContainerItem, updateSelectedContainerItem } from '../Reducers/Configurator/Actions';
import getImageUrl from '@configuratorware/configurator-frontendgui/App/Utils/getImageUrl';

const useStyles = makeStyles((theme) => ({
  popoverRoot: { pointerEvents: 'none' },
  paper: {
    pointerEvents: 'all',
    minWidth: '320px !important',
    maxWidth: '380px !important',
    borderRadius: 4,
    boxShadow: '0px 5px 15px rgba(0,0,0,0.2)',
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    flexDirection: 'column',
    height: 'auto',
    maxHeight: 'calc(100vh - 100px)',
  },
  dialogContent: {
    padding: '0 !important',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    flex: '1 1 auto',
  },
  scrollableList: {
    padding: theme.spacing(2),
    overflowY: 'auto',
    flex: '1 1 auto',
    minHeight: '100px',
  },
  footerSection: {
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    zIndex: 10,
    flexShrink: 0,
  },
  sectionTitle: {
    fontWeight: 'bold',
    fontSize: '1rem',
    color: theme.palette.text.primary,
  },
  activeState: {
    backgroundColor: `${fade(theme.palette.primary.main, 0.15)} !important`,
    border: `2px solid ${theme.palette.primary.main} !important`,
    color: `${theme.palette.text.primary} !important`,
    fontWeight: 'bold',
    '& $optionLabel': {
      color: theme.palette.text.primary,
      fontWeight: 700,
    },
    '& $connectLabel': {
      color: theme.palette.text.primary,
    },
    '&:hover': {
      backgroundColor: `${fade(theme.palette.primary.main, 0.25)} !important`,
    },
  },
  optionButton: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: theme.spacing(1, 1),
    marginBottom: theme.spacing(1),
    backgroundColor: 'transparent',
    color: theme.palette.text.secondary,
    textTransform: 'none',
    borderRadius: 4,
    border: '2px solid transparent',
    transition: 'all 0.2s ease-in-out',
    '&:hover': { backgroundColor: theme.palette.action.hover },
  },
  optionImage: {
    width: '180px',
    height: '72px',
    marginRight: theme.spacing(2),
    objectFit: 'contain',
  },
  optionLabel: {
    fontSize: '1rem',
    textAlign: 'left',
    fontWeight: 500,
    color: theme.palette.text.primary,
    whiteSpace: 'normal',
    lineHeight: 1.2,
  },
  accordionRoot: {
    boxShadow: 'none',
    margin: '0 !important',
    '&:before': { display: 'none' },
    backgroundColor: 'transparent',
  },
  accordionSummary: {
    padding: theme.spacing(0, 2),
    minHeight: '48px !important',
    '& .MuiAccordionSummary-content': {
      margin: '12px 0 !important',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
  },
  accordionDetails: {
    padding: theme.spacing(0, 2, 2, 2),
    display: 'block',
  },
  connectWrapper: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  connectButton: {
    width: '100%',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(2),
    textTransform: 'none',
    color: theme.palette.text.secondary,
    border: '2px solid transparent',
    borderRadius: 4,
    transition: 'all 0.2s ease-in-out',
    '&:hover': { backgroundColor: theme.palette.action.hover },
    '&:disabled': {
      border: `1px solid ${theme.palette.divider}`,
    },
    '&:disabled img': {
      opacity: 0.3,
      filter: 'grayscale(100%)',
    },
    '&:disabled span': {
      color: theme.palette.action.disabled,
    },
  },
  connectImage: {
    width: '100%',
    maxWidth: '80px',
    paddingRight: theme.spacing(1),
    height: 'auto',
    marginBottom: theme.spacing(1),
    objectFit: 'contain',
  },
  connectLabel: {
    fontSize: '0.9rem',
    lineHeight: 1.3,
    textAlign: 'center',
    fontWeight: 500,
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.primary,
  },
}));

const ContainerDialog = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const selectedContainer = useSelector((state) => state.configurator?.selectedContainerItem || null);
  const options = useSelector((state) => state.configurator?.options || {});
  const baseContainer = useSelector((state) => state.configurator?.configuration?.customdata?.baseContainer);

  const open = selectedContainer !== null;
  const anchorRef = React.useRef(null);
  const scrollContainerRef = useRef(null);

  const handleClose = () => {
    dispatch(setSelectedContainerItem(null));
  };

  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  const lastColumn = baseContainer?.columns ? baseContainer.columns - 1 : 0;
  const isRotated = selectedContainer && selectedContainer.row === 1;
  const enableVisualLeft = selectedContainer && selectedContainer.column > 0;
  const enableVisualRight = selectedContainer && selectedContainer.column < lastColumn;
  const isLeftConnected =
    selectedContainer && (isRotated ? !selectedContainer.rightWall : !selectedContainer.leftWall);
  const isRightConnected =
    selectedContainer && (isRotated ? !selectedContainer.leftWall : !selectedContainer.rightWall);

  return (
    <>
      <div ref={anchorRef} style={{ position: 'absolute', top: 120, left: 20, width: 0, height: 0 }} />
      <Popover
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        classes={{ root: classes.popoverRoot, paper: classes.paper }}
        disableScrollLock
      >
        <MenuTitleBar title={t(`containerDialog.title`)} onClose={handleClose} />
        <DialogContent className={classes.dialogContent}>
          <div className={classes.scrollableList} ref={scrollContainerRef}>
            <Typography className={classes.sectionTitle}>
              {t(`containerDialog.sectionLabels.moduleType`)}
            </Typography>
            <div>
              {options.map((option) => {
                const isSelected =
                  selectedContainer && selectedContainer.selectedOption === option.identifier;
                return (
                  <Button
                    key={option.identifier}
                    className={`${classes.optionButton} ${isSelected ? classes.activeState : ''}`}
                    onClick={() =>
                      dispatch(
                        updateSelectedContainerItem({
                          ...selectedContainer,
                          selectedOption: option.identifier,
                        })
                      )
                    }
                    disableElevation
                    disableRipple
                  >
                    <img
                      src={getImageUrl(`/images/options/${option.identifier}.png`)}
                      alt={option.title}
                      className={classes.optionImage}
                    />
                    <span className={classes.optionLabel}>{option.title}</span>
                  </Button>
                );
              })}
            </div>
          </div>
          {baseContainer.columns > 1 && (
            <div className={classes.footerSection}>
              <Accordion
                defaultExpanded={false}
                className={classes.accordionRoot}
                TransitionProps={{ onEntered: scrollToBottom }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel-content"
                  id="panel-header"
                  className={classes.accordionSummary}
                >
                  <Typography className={classes.sectionTitle}>
                    {t(`containerDialog.sectionLabels.connectModules`)}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className={classes.accordionDetails}>
                  <div className={classes.connectWrapper}>
                    <Button
                      disabled={!enableVisualLeft}
                      className={`${classes.connectButton} ${isLeftConnected && enableVisualLeft ? classes.activeState : ''}`}
                      onClick={() =>
                        dispatch(
                          updateSelectedContainerItem({
                            ...selectedContainer,
                            [isRotated ? 'rightWall' : 'leftWall']: isRotated
                              ? !selectedContainer.rightWall
                              : !selectedContainer.leftWall,
                          })
                        )
                      }
                    >
                      <img
                        src={getImageUrl('/images/options/connect_to_left.png')}
                        alt={t('containerDialog.moduleTypes.connectToLeft')}
                        className={classes.connectImage}
                      />
                      <span
                        className={classes.connectLabel}
                        dangerouslySetInnerHTML={{
                          __html: t('containerDialog.moduleTypes.connectToLeft'),
                        }}
                      />
                    </Button>
                    <Button
                      disabled={!enableVisualRight}
                      className={`${classes.connectButton} ${isRightConnected && enableVisualRight ? classes.activeState : ''}`}
                      onClick={() =>
                        dispatch(
                          updateSelectedContainerItem({
                            ...selectedContainer,
                            [isRotated ? 'leftWall' : 'rightWall']: isRotated
                              ? !selectedContainer.leftWall
                              : !selectedContainer.rightWall,
                          })
                        )
                      }
                    >
                      <img
                        src={getImageUrl('/images/options/connect_to_right.png')}
                        alt={t('containerDialog.moduleTypes.connectToRight')}
                        className={classes.connectImage}
                      />
                      <span
                        className={classes.connectLabel}
                        dangerouslySetInnerHTML={{
                          __html: t('containerDialog.moduleTypes.connectToRight'),
                        }}
                      />
                    </Button>
                  </div>
                </AccordionDetails>
              </Accordion>
            </div>
          )}
        </DialogContent>
      </Popover>
    </>
  );
};

export default ContainerDialog;
